import { analyticsService } from '../analyticsService';

const RELOAD_MARKER_KEY = 'analytics.programmaticReload';
const LAST_LOAD_KEY = 'analytics.lastLoad';

interface LastLoad {
  url: string;
  at: number;
  reloads: number;
}

/**
 * Records that the next load was triggered by the app, not by the user. The app reloads
 * itself for bfcache restores, chunk failures and auth recovery; without this they are
 * indistinguishable from someone pressing refresh.
 */
export const markProgrammaticReload = (reason: string): void => {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, reason);
  } catch {
    // Storage disabled — the reload is reported as user-initiated.
  }
};

const takeReloadReason = (): string | null => {
  try {
    const reason = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
    if (reason !== null) window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
    return reason;
  } catch {
    return null;
  }
};

const readLastLoad = (): LastLoad | null => {
  try {
    const raw = window.sessionStorage.getItem(LAST_LOAD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastLoad>;
    if (typeof parsed?.url !== 'string' || typeof parsed?.at !== 'number') return null;
    return { url: parsed.url, at: parsed.at, reloads: parsed.reloads ?? 0 };
  } catch {
    return null;
  }
};

const writeLastLoad = (value: LastLoad): void => {
  try {
    window.sessionStorage.setItem(LAST_LOAD_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const getNavigationType = (): string => {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type ?? 'navigate';
};

let reported = false;

/**
 * Emits one `page_loaded` event per document load. Refreshes are
 * `navigation_type = 'reload'` with `reload_source = 'user'`; reporting every load gives
 * the denominator, so a route can be judged on refresh *rate* rather than raw count.
 */
export const trackPageLoad = (): void => {
  if (reported || typeof window === 'undefined') return;
  reported = true;

  const navigationType = getNavigationType();
  const url = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams(window.location.search);
  const now = Date.now();

  const appReason = takeReloadReason();
  const previous = readLastLoad();
  const isReload = navigationType === 'reload';

  // Repeat refreshes of the same URL are the strongest signal that something is stuck.
  // 0 means "not a reload"; a reload that happened is always at least 1.
  let consecutiveReloads = 0;
  if (isReload) consecutiveReloads = previous?.url === url ? previous.reloads + 1 : 1;
  writeLastLoad({ url, at: now, reloads: consecutiveReloads });

  void analyticsService.trackEvent('page_loaded', {
    navigation_type: navigationType,
    route: url,
    panel: params.get('panel') ?? undefined,
    tab: params.get('tab') ?? undefined,
    view: params.get('view') ?? undefined,
    reload_source: isReload ? (appReason ? 'app' : 'user') : undefined,
    reload_reason: appReason ?? undefined,
    consecutive_reloads: consecutiveReloads,
    ms_since_previous_load: previous ? now - previous.at : undefined,
  });
};

/** Test seam. `keepStorage` simulates a fresh document within the same browser session. */
export const resetPageLoadForTests = ({ keepStorage = false } = {}): void => {
  reported = false;
  if (keepStorage) return;
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
    window.sessionStorage.removeItem(LAST_LOAD_KEY);
  } catch {
    // ignore
  }
};
